import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Milk, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t } = useI18n();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Milk className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-bold">MilkERP</span>
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">{t("reset.title")}</h2>
        <p className="mb-6 mt-1 text-sm text-muted-foreground">{t("reset.subtitle")}</p>
        {!token && <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">Missing reset token.</div>}
        {error && <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">{error}</div>}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pw">{t("reset.new")}</Label>
            <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpw">{t("reset.confirm")}</Label>
            <Input id="cpw" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
          </div>
          <Button type="submit" className="w-full" disabled={loading || !token} data-testid="reset-submit-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("reset.update")}
          </Button>
        </form>
        <Link to="/login" className="mt-6 block text-center text-sm text-muted-foreground hover:text-foreground">{t("forgot.back")}</Link>
      </div>
    </div>
  );
}
